import styled from '@emotion/styled'
import { Card, DESKTOP_BREAKPOINT, straitsXTheme, useModal, useTheme } from '@xfers/design-system'
import React, { Suspense, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { newStraitsXRoutes } from '~/biz/routes/config'
import { selectMerchantDetail, selectUserId } from '~/biz/store/merchantInfo/selectors'
import { RootState } from '~/biz/store/types'
import Dpt2Tnc from '~/components/Dpt2Tnc'
import Dpt2LockedPage from '~/components/Dpt2Tnc/Dpt2LockedPage'
import { ErrorBoundary } from '~/components/ErrorBoundary/ErrorBoundary'
import KillSwitchLockedPage from '~/components/KillSwitchLockedPage'
import PageLoader from '~/components/PageLoader'
import { PrivateRoute, RouteHooks } from '~/components/Route'
import SandboxBanner from '~/components/SandBoxBanner'
import StraitsXFooter from '~/components/StraitsXFooter'
import StraitsXNavigation from '~/components/StraitsXNavigation'
import StraitsXUserInfoBar from '~/components/StraitsXUserInfoBar'
import { createGaEvent, createGaPageView } from '~/helpers/gaHelper'
import { useAutoAddPosthogIds } from '~/hooks/useAutoAddPosthogIds'
import { useShield } from '~/hooks/useShield'
import { SITE_TYPE } from '~/managers/ConfigManager'
import { ModeManager } from '~/managers/ModeManager'
import { useBizFlippers, useRootFlippers } from '~/store/flippers/hooks'
import { selectUserEmail } from '~/store/route/selectors'
import { USER_ROLES } from '~/types'
import { useInitRoot } from './RootHooks'
import { PATHS } from './routes/paths'
import { selectHasBusinessAccount } from './store/accounts/selectors'
import { selectUserProfile } from './store/merchantInfo/selectors'
import { selectFeatureMatrixFactors, selectIsFiatWalletOnly } from './store/route/selectors'

export default () => {
  const { resetModal } = useModal()
  const history = useHistory()
  const location = useLocation()
  const email = useSelector(selectUserEmail)
  useAutoAddPosthogIds(SITE_TYPE.BIZ)
  useBizFlippers({ email })

  const isBusinessAccount = useSelector(selectHasBusinessAccount)
  const { merchantDetail } = useSelector(selectMerchantDetail)
  const userId = useSelector(selectUserId)

  const userProfile = useSelector(selectUserProfile)

  useRootFlippers()
  useShield(userId)

  const {
    isDeveloperToolEnabled: displayDevToolTab,
    isSandboxEnabled: displayModeSwitcher,
    role,
  } = useSelector(selectFeatureMatrixFactors)

  const { isAccountStatementUIEnabled } = useSelector(
    (state: RootState) => state.straitsXAccountStatement
  )

  const isFiatWalletOnly = useSelector(selectIsFiatWalletOnly)

  // fetching application needed data
  const {
    displayGetStartedTab,
    isCardsDashboardEnabled,
    isInitiated,
    isKillSwitchActive,
    isStraitsxMenuDisabled,
    notifications,
    shouldRenderCardsDashboard,
    shouldRenderDpt2Tnc,
    shouldRenderManageAccounts,
  } = useInitRoot()

  // on sandbox<->production mode switched
  const onModeSwitched = () => {
    // TODO: think about better solution
    window.location.reload()
  }

  const isSandbox = ModeManager.isSandboxMode()

  history.listen(loc => {
    const urlPath = loc.pathname + loc.search
    createGaPageView(urlPath)
    createGaEvent('URL', urlPath, 'Changed URL')
  })

  const memoizedNav = useMemo(() => {
    return (
      <StraitsXNavigation
        displayEarnTab={!isFiatWalletOnly && !!userProfile?.merchantId}
        displayGetStartedTab={displayGetStartedTab}
        displayDevToolTab={displayDevToolTab}
        displayAccountStatementTab={isAccountStatementUIEnabled}
        displayUserCardsTab={isCardsDashboardEnabled}
        disableStraitsxMenu={isStraitsxMenuDisabled}
        isSandbox={isSandbox}
        isBusinessAccount={isBusinessAccount}
      />
    )
  }, [
    isSandbox,
    displayGetStartedTab,
    isBusinessAccount,
    displayDevToolTab,
    isAccountStatementUIEnabled,
    isFiatWalletOnly,
    userProfile?.merchantId,
  ])

  if (!isInitiated) {
    return <PageLoader />
  }

  if (isKillSwitchActive) {
    return <KillSwitchLockedPage />
  }

  if (shouldRenderDpt2Tnc) {
    if (role === USER_ROLES.OWNER) {
      return <Dpt2Tnc siteType={SITE_TYPE.BIZ} />
    }
    return <Dpt2LockedPage />
  }

  return (
    <Root id="biz-root">
      <RouteHooks resetModal={resetModal} />

      {isKillSwitchActive !== undefined && (
        <Container>
          <NavigationWrapper>{memoizedNav}</NavigationWrapper>
          <MainContainer isSandbox={isSandbox}>
            <ErrorBoundary key={location.pathname}>
              {isSandbox && <SandboxBanner />}
              <StraitsXUserInfoBar
                userInfo={{
                  fullName: userProfile?.fullName || '',
                  avatarUrl: userProfile?.avatarUrl || '',
                  displayname: merchantDetail?.businessName || '',
                }}
                isSandbox={isSandbox}
                notifications={notifications}
                displayGetStartedTab={displayGetStartedTab}
                displayDevToolTab={displayDevToolTab}
                displayAccountStatementTab={isAccountStatementUIEnabled}
                displayEarnTab={!isFiatWalletOnly && !!userProfile?.merchantId}
                displayModeSwitcher={displayModeSwitcher}
                displayUserCardsTab={isCardsDashboardEnabled}
                disableStraitsxMenu={isStraitsxMenuDisabled}
                isBusinessAccount={isBusinessAccount}
                onChangeModeCallback={onModeSwitched}
              />

              <Suspense fallback={<LoadingPage />}>
                <Switch>
                  {shouldRenderCardsDashboard && (
                    <Route exact path={PATHS.LANDING_PAGE}>
                      <Redirect to={PATHS.USER_CARDS} />
                    </Route>
                  )}
                  <Route path={PATHS.VERIFICATION}>
                    <Redirect to={PATHS.LANDING_PAGE} />
                  </Route>
                  {shouldRenderManageAccounts && (
                    <Route path={PATHS.MANAGE_ACCOUNT}>
                      <Redirect to={PATHS.LANDING_PAGE} />
                    </Route>
                  )}
                  {newStraitsXRoutes.map((route, i) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <PrivateRoute key={i} {...route} />
                  ))}
                </Switch>
              </Suspense>

              <StraitsXFooter />
            </ErrorBoundary>
          </MainContainer>
        </Container>
      )}
    </Root>
  )
}

function LoadingPage() {
  const { spacing } = useTheme()

  return (
    <Page>
      <Card style={{ padding: spacing.xl }}>
        <Skeleton style={{ width: '40%' }} />
        <Skeleton />
        <Skeleton />
      </Card>
    </Page>
  )
}

const Page = styled.div`
  padding: 16px;
  background: none;

  ${DESKTOP_BREAKPOINT} {
    background-color: ${straitsXTheme.color.background.secondaryNeutral};
    padding: 24px;
  }
`

const Root = styled.div`
  z-index: 0;
`

const Container = styled.div`
  background-size: 100%;
  background-color: ${straitsXTheme.color.background.secondaryNeutral};
  -webkit-font-smoothing: antialiased;
  display: flex;
`

const MainContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 73px;
  width: 100%;

  ${DESKTOP_BREAKPOINT} {
    padding-top: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '73px' : 0)};
  }
`

const NavigationWrapper = styled.div`
  z-index: 1;
  display: none;

  ${DESKTOP_BREAKPOINT} {
    display: flex;
    width: 257px;
    flex-shrink: 0;
  }
`

const Skeleton = styled.div`
  float: left;
  width: 100%;
  height: 16px;

  margin: 10px;
  border-radius: 5px;
  background-size: 100wh;
  background-image: linear-gradient(
    90deg,
    ${straitsXTheme.color.base.line} 0%,
    ${straitsXTheme.color.background.secondaryNeutral} 50%,
    ${straitsXTheme.color.base.line} 100%
  );
  animation: shine-lines 1s infinite linear;

  @keyframes shine-lines {
    0% {
      background-position: -100px;
    }
    40%,
    100% {
      background-position: 140px;
    }
  }
`
